import { useCallback, useEffect, useMemo, useState } from "react";
import { CampaignServiceError, getCampaignHierarchy } from "../api";
import {
  CampaignHierarchyItem,
  OpportunityHierarchyItem,
  ProgramHierarchyItem,
} from "../types";

interface UseUpdateProgramDataResult {
  campaign: CampaignHierarchyItem | null;
  programs: ProgramHierarchyItem[];
  selectedProgram: ProgramHierarchyItem | null;
  selectedProgramId: string;
  opportunities: OpportunityHierarchyItem[];
  loading: boolean;
  error: string | null;
  setSelectedProgramId: (programId: string) => void;
  retry: () => Promise<void>;
}

export function useUpdateProgramData(
  campaignId: string,
  initialProgramId?: string,
): UseUpdateProgramDataResult {
  const [campaign, setCampaign] = useState<CampaignHierarchyItem | null>(null);
  const [programs, setPrograms] = useState<ProgramHierarchyItem[]>([]);
  const [selectedProgramId, setSelectedProgramId] = useState(initialProgramId || "");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadProgramData = useCallback(async () => {
    if (!campaignId) {
      setCampaign(null);
      setPrograms([]);
      setSelectedProgramId("");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const hierarchy = await getCampaignHierarchy();
      const currentCampaign = hierarchy.campaigns.find(
        (item) => item.id === campaignId,
      );

      const nextPrograms = currentCampaign?.programs ?? [];
      const nextSelectedProgramId =
        nextPrograms.find((program) => program.id === selectedProgramId)?.id ||
        nextPrograms.find((program) => program.id === initialProgramId)?.id ||
        nextPrograms[0]?.id ||
        "";

      setCampaign(currentCampaign || null);
      setPrograms(nextPrograms);
      setSelectedProgramId(nextSelectedProgramId);
    } catch (loadError) {
      setCampaign(null);
      setPrograms([]);
      setSelectedProgramId("");
      setError(
        loadError instanceof CampaignServiceError || loadError instanceof Error
          ? loadError.message
          : "Failed to load campaign program data.",
      );
    } finally {
      setLoading(false);
    }
  }, [campaignId, initialProgramId, selectedProgramId]);

  useEffect(() => {
    void loadProgramData();
  }, [loadProgramData]);

  const selectedProgram = useMemo<ProgramHierarchyItem | null>(() => {
    if (!programs.length) {
      return null;
    }

    return programs.find((program) => program.id === selectedProgramId) || programs[0] || null;
  }, [programs, selectedProgramId]);

  const opportunities = useMemo<OpportunityHierarchyItem[]>(
    () => selectedProgram?.opportunities ?? [],
    [selectedProgram],
  );

  return {
    campaign,
    programs,
    selectedProgram,
    selectedProgramId,
    opportunities,
    loading,
    error,
    setSelectedProgramId,
    retry: loadProgramData,
  };
}
